import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type ComponentProps
} from "react"
import { Capacitor } from "@capacitor/core"
import { usePostHog } from "posthog-js/react"
import { toast } from "sonner"
import { DefaultThreadStoreAuth } from "@blocknote/core/comments"
import { RESTYjsThreadStore } from "@blocknote/core/yjs"
import { authClient } from "~/lib/auth-client"
import { defaultThemePreference } from "~/lib/theme"
import { useTheme } from "~/components/providers/Theme"
import { api } from "~/trpc/react"
import {
	collaborationColorTokens,
	resolveCollaborationColor
} from "./CollaborationCursor"
import { acquireCollabRoom, releaseCollabRoom } from "./collaboration"
import { Editor } from "./Editor"

/*
 * Collaborative editor surface: joins the server's y-websocket room (which
 * persists committed state), shares awareness, and — for knowledge-base
 * documents — connects BlockNote comments through server-authorized REST
 * thread endpoints. Read-only viewers without comment permission keep the
 * plain editor (BlockNote marks comments in the document schema).
 */
export function CollaborativeEditor({
	kind,
	id,
	teamId,
	commentRole,
	onSaved,
	...props
}: Omit<
	ComponentProps<typeof Editor>,
	"collaboration" | "teamId" | "children"
> & {
	kind: "issue" | "document" | "form"
	id: string
	teamId: string
	/** From permissions: "editor" for EDIT_KNOWLEDGE_BASE, "comment" for COMMENT_KNOWLEDGE_BASE. */
	commentRole?: "comment" | "editor"
	onSaved?: () => void
}) {
	const { data: authSession } = authClient.useSession()
	const posthog = usePostHog()
	const { preference } = useTheme()
	const [identityColor, setIdentityColor] = useState({
		color: defaultThemePreference.accent,
		colorToken: "--color-orbit"
	})
	useEffect(() => {
		if (!authSession) return
		let hash = 0
		for (const character of authSession.user.id)
			hash = (Math.imul(hash, 31) + character.charCodeAt(0)) | 0
		const colorToken =
			collaborationColorTokens[
				Math.abs(hash) % collaborationColorTokens.length
			]!
		setIdentityColor({
			color: resolveCollaborationColor(colorToken).color,
			colorToken
		})
	}, [authSession?.user.id, preference])

	const native = Capacitor.isNativePlatform()
	/*
	 * Outage UX: only surface a failure after a sustained outage (brief deploy
	 * blips and idle-timeout closes recover silently within the grace window),
	 * once per outage, and confirm the recovery once state syncs again.
	 */
	const outageToastDelayMs = 20_000
	const outageToastId = `editor-collaboration-${kind}-${id}`
	const savedRef = useRef(onSaved)
	savedRef.current = onSaved

	// Committed persistence notifications refresh the page's authoritative data.
	const commitSubscription =
		kind === "issue"
			? "issueUpdates"
			: kind === "form"
				? "formUpdates"
				: "documentUpdates"
	api.collaboration[commitSubscription].useSubscription(
		{ teamId, id },
		{
			enabled: !native && !!authSession,
			onData: () => savedRef.current?.()
		}
	)

	const commentUsers = api.collaboration.threadUsers.useQuery(
		{ teamId },
		{ enabled: !native && !!commentRole && !!authSession }
	)

	const [connectionVersion, setConnectionVersion] = useState(0)
	const [connection, setConnection] = useState<ReturnType<
		typeof acquireCollabRoom
	> | null>(null)
	useEffect(() => {
		if (native || !authSession) return
		const value = acquireCollabRoom({ kind, id })
		setConnection(value)
		setConnectionVersion((version) => version + 1)
		return () => releaseCollabRoom(kind, id)
	}, [kind, id, native, authSession?.user.id])

	useEffect(() => {
		if (!connection) return
		let stopped = false
		let offlineSince: number | null = null
		let outageTimer: ReturnType<typeof setTimeout> | null = null
		let outageNotified = false

		const observeSync = (synced: boolean) => {
			if (stopped || !synced || !connection.provider.wsconnected) return
			if (outageTimer) {
				clearTimeout(outageTimer)
				outageTimer = null
			}
			offlineSince = null
			if (outageNotified) {
				outageNotified = false
				toast.success("Live collaboration restored.", {
					id: outageToastId
				})
			}
			savedRef.current?.()
			posthog.capture("editor_collaboration_connected", {
				kind,
				id,
				teamId,
				transport: "websocket"
			})
		}
		const observeDisconnect = (event: Event | null) => {
			if (stopped) return
			const code = event && "code" in event ? event.code : undefined
			if (typeof code === "number" && code >= 4400 && code < 4500) {
				// Terminal: access is gone and the provider stops reconnecting.
				if (outageTimer) {
					clearTimeout(outageTimer)
					outageTimer = null
				}
				toast.error(
					"Live collaboration cannot access this document. Check your sign-in and document access.",
					{ id: outageToastId }
				)
				posthog.capture("editor_collaboration_access_denied", {
					kind,
					id,
					teamId,
					code
				})
				return
			}
			offlineSince ??= Date.now()
			if (outageTimer || outageNotified) return
			outageTimer = setTimeout(() => {
				outageTimer = null
				if (stopped || offlineSince === null) return
				outageNotified = true
				toast.error(
					"Reconnecting to live collaboration. Keep this tab open to preserve unsynced edits.",
					{ id: outageToastId, duration: Number.POSITIVE_INFINITY }
				)
				posthog.capture("editor_collaboration_connection_failed", {
					kind,
					id,
					teamId,
					offlineMs: Date.now() - offlineSince
				})
			}, outageToastDelayMs)
		}
		connection.provider.on("sync", observeSync)
		connection.provider.on("connection-error", observeDisconnect)
		connection.provider.on("connection-close", observeDisconnect)
		if (connection.provider.synced) observeSync(true)
		else observeDisconnect(null)
		return () => {
			stopped = true
			if (outageTimer) clearTimeout(outageTimer)
			toast.dismiss(outageToastId)
			connection.provider.off("connection-error", observeDisconnect)
			connection.provider.off("connection-close", observeDisconnect)
			connection.provider.off("sync", observeSync)
		}
	}, [connection, connectionVersion, kind, id, teamId, posthog])

	const commentsOptions = useMemo(() => {
		if (!commentRole || !connection || !authSession) return undefined
		return {
			threadStore: new RESTYjsThreadStore(
				`/api/collab/threads/${kind}/${id}`,
				{},
				connection.doc.getMap("threads"),
				new DefaultThreadStoreAuth(authSession.user.id, commentRole)
			),
			resolveUsers: async (userIds: string[]) => {
				const users = commentUsers.data ?? []
				return userIds.flatMap((userId) => {
					const found = users.find((user) => user.id === userId)
					return found ? [found] : []
				})
			}
		}
	}, [
		commentRole,
		connection,
		kind,
		id,
		authSession?.user.id,
		commentUsers.data
	])

	if (native || !connection || !authSession)
		return <Editor {...props} teamId={teamId} />

	return (
		<Editor
			{...props}
			teamId={teamId}
			collaboration={{
				fragment: connection.fragment,
				provider: connection.provider,
				user: {
					name: authSession.user.name,
					...identityColor
				},
				showCursorLabels: "activity"
			}}
			comments={commentsOptions}
		/>
	)
}
